import { useMutation, useQuery } from '@tanstack/react-query';
import useApi from 'api/mavapi/useApi';
import { useAccessToken } from './useAccessToken';
import { RefreshResponseDto, ReportsService } from 'api/openapi';
import React from 'react';
import { useDispatch } from 'react-redux';
import { useErrorHandlingSlice } from 'app/error/slice';

const useReportApi = () => {
  const { handleRequest } = useApi();
  const accessToken = useAccessToken();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const dispatch = useDispatch();
  const { actions } = useErrorHandlingSlice();

  const useMasterDataList = (reportId?: string, workspaceId?: string) => {
    return useQuery<any>({
      queryKey: ['masterDataList', reportId, workspaceId],
      queryFn: () => {
        const response = handleRequest(
          ReportsService.getApiReports(workspaceId, reportId),
        );
        return response;
      },
      enabled: !!accessToken,
      refetchOnWindowFocus: false,
      // Only run the query if accessToken doesn't exist
    });
  };

  const useRefreshMasterDataList = (
    reportId?: string,
    workspaceId?: string,
    handleRecall?: () => void,
  ) => {
    // Create a mutation for handling refresh delay directly within this hook
    const refreshDelayMutation = useMutation<RefreshResponseDto, Error, void>({
      mutationFn: async () => {
        setIsLoading(true);
        return await handleRequest(
          ReportsService.postApiReportsRefreshDelay(workspaceId, reportId),
        );
      },
      onSuccess: res => {
        setIsLoading(false);
        if (res.status) {
          if (res.status === 'Success') {
            handleRecall?.();
          } else if (res.status === 'Error') {
            dispatch(
              actions.dispatchToast({
                errorTxt: `${'Please try again after some time.'}`,
                show: true,
                errorType: 'error',
              }),
            );
            dispatch(actions.setToastDelay(5000));
          } else if (res.status === 'In Progress') {
            dispatch(
              actions.dispatchToast({
                errorTxt: `${res.message ?? 'Something went wrong'}`,
                show: true,
                errorType: 'error',
              }),
            );
            dispatch(actions.setToastDelay(5000));
          }
        }
      },
    });

    return useMutation<RefreshResponseDto, Error, void>({
      mutationFn: async () => {
        setIsLoading(true);
        return await handleRequest(
          ReportsService.postApiReportsRefresh(workspaceId, reportId),
        );
      },
      onSuccess: res => {
        setIsLoading(false);
        if (res) {
          if (res.status === 'Success') {
            handleRecall?.();
          } else if (res.status === 'Error') {
            dispatch(
              actions.dispatchToast({
                errorTxt: `${'Please try again after some time.'}`,
                show: true,
                errorType: 'error',
              }),
            );
            dispatch(actions.setToastDelay(5000));
          } else if (res.status === 'In Progress') {
            dispatch(
              actions.dispatchToast({
                errorTxt: `${res.message ?? 'Something went wrong'}`,
                show: true,
                errorType: 'error',
              }),
            );
            dispatch(actions.setToastDelay(5000));
            // Call the mutate function from the refresh delay mutation
            refreshDelayMutation.mutate();
          }
        }
      },
    });
  };

  return {
    useMasterDataList,
    useRefreshMasterDataList,
    isLoading,
  };
};

export default useReportApi;
